import { type MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';

import { showWinchToast } from './winchMqttToasts';

/** MQTT command replies are usually fast; fail before the 60s status poll. */
export const SET_LIMITS_ACK_TIMEOUT_MS = 10_000;

export function useSetLimitsAck(input: {
    isConnected: boolean;
    tRef: MutableRefObject<(key: string) => string>;
}): {
    setLimitsPending: boolean;
    setLimitsFailed: boolean;
    beginSetLimitsPending: (requestId: string) => void;
    settleSetLimits: (failed: boolean, requestId?: string | null) => boolean;
    failSetLimits: () => void;
    resetSetLimitsAck: () => void;
} {
    const { isConnected, tRef } = input;
    const [setLimitsPending, setSetLimitsPending] = useState(false);
    const [setLimitsFailed, setSetLimitsFailed] = useState(false);
    const timeoutRef = useRef<number | null>(null);
    const pendingRef = useRef(false);
    const pendingUuidRef = useRef<string | null>(null);

    const clearAckTimeout = useCallback((): void => {
        if (timeoutRef.current != null) {
            window.clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    }, []);

    const applySettle = useCallback(
        (failed: boolean): void => {
            clearAckTimeout();
            pendingRef.current = false;
            pendingUuidRef.current = null;
            setSetLimitsPending(false);
            setSetLimitsFailed(failed);
        },
        [clearAckTimeout]
    );

    const settleSetLimits = useCallback(
        (failed: boolean, requestId?: string | null): boolean => {
            if (!pendingRef.current || requestId == null || requestId !== pendingUuidRef.current) {
                return false;
            }
            applySettle(failed);
            return true;
        },
        [applySettle]
    );

    const failSetLimits = useCallback((): void => {
        applySettle(true);
    }, [applySettle]);

    const failSetLimitsWithToast = useCallback((): void => {
        if (!pendingRef.current) {
            return;
        }
        applySettle(true);
        showWinchToast(tRef.current('winchControl.toast.scanError'), 'error', 'winch-scan-error');
    }, [applySettle, tRef]);

    const beginSetLimitsPending = useCallback(
        (requestId: string): void => {
            clearAckTimeout();
            pendingRef.current = true;
            pendingUuidRef.current = requestId;
            setSetLimitsFailed(false);
            setSetLimitsPending(true);
            timeoutRef.current = window.setTimeout(() => {
                timeoutRef.current = null;
                failSetLimitsWithToast();
            }, SET_LIMITS_ACK_TIMEOUT_MS);
        },
        [clearAckTimeout, failSetLimitsWithToast]
    );

    const resetSetLimitsAck = useCallback((): void => {
        clearAckTimeout();
        pendingRef.current = false;
        pendingUuidRef.current = null;
        setSetLimitsPending(false);
        setSetLimitsFailed(false);
    }, [clearAckTimeout]);

    useEffect(() => {
        if (!setLimitsPending) {
            return;
        }
        if (isConnected) {
            return;
        }
        failSetLimitsWithToast();
    }, [failSetLimitsWithToast, isConnected, setLimitsPending]);

    useEffect(() => () => clearAckTimeout(), [clearAckTimeout]);

    return {
        setLimitsPending,
        setLimitsFailed,
        beginSetLimitsPending,
        settleSetLimits,
        failSetLimits,
        resetSetLimitsAck,
    };
}
